import _ from "lodash";
import { useEffect, useLayoutEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { COMMAND_ID as SELECT_ENTITY_COMMAND_ID } from "@/app/app/_/commands/SelectEntityCommand";
import { NodeLayout } from "@/components/organisms/Node/NodeLayout";
import { NodeSubmitButton } from "@/components/organisms/Node/NodeSubmitButton";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import { useNode, useNodeSetup } from "@/components/organisms/Node/Node.hooks";
import { createNodeFieldsStore } from "@/components/organisms/Node/Node.store";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { useScope } from "@/hooks/useScope";
import { CommandCenterContextEntityPicker } from "@/layouts/AppLayout/CommandCenter/CommandCenterContextEntityPicker";
import { Objects } from "@/objects";
import { IObject } from "@/__generated__/graphql";
import { State } from "@/state";
import { useGlobalStore } from "@/state/useGlobalStore";
import { Button } from "@/components/ui/button";

export const COMMAND_ID_PREFIX = "create-";

export type CreateNodeContextType = {
  entityIdentifier?: string;
  /**
   * Values the form opens with, for a record another flow has already worked
   * out (a rule made from a triage charge). Root keys starting with ``_`` are
   * for the form's own groups and never sent.
   */
  initialFieldValues?: Record<string, unknown>;
  /** Runs with the created record once the server has it. */
  onCreated?: (node: IObject) => void;
};

const stores = new Map<string, ReturnType<typeof createNodeFieldsStore>>();

const getStore = (instanceId: string) => {
  if (stores.has(instanceId)) {
    return stores.get(instanceId);
  }
  const store = createNodeFieldsStore("create");
  stores.set(instanceId, store);
  return store;
};

function Header({ commandInstanceId }: { commandInstanceId: string }) {
  const store = getStore(commandInstanceId);
  const { metadata } = useNode(store);

  return (
    <NodeTitleLayout
      title={metadata.title}
      className="w-[calc(100%)] pl-0 pr-0 bg-popover"
      mode="create"
      store={store}
    />
  );
}

function Footer({
  commandInstanceId,
  commandDefinitionId,
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: CreateNodeContextType;
}) {
  const store = getStore(commandInstanceId);
  const { events } = useNode(store);
  const clearCommandStack = State((state) => state.clearCommandStack);
  const popCommandsFromStack = State((state) => state.popCommandsFromStack);

  return (
    <div className="flex gap-2 flex-wrap flex-row items-center">
      <Button variant="outline" onClick={() => popCommandsFromStack()}>
        Cancel
      </Button>
      <NodeSubmitButton
        store={store}
        onSubmit={async () => {
          const submitted = events.onSubmitNode();
          clearCommandStack();
          if (!context?.onCreated) return;
          const result = (await submitted) as {
            data?: { createNode?: { data?: IObject[] } };
          };
          const created = result?.data?.createNode?.data?.[0];
          if (created) context.onCreated(created);
        }}
      >
        Create
      </NodeSubmitButton>
    </div>
  );
}

function Component({
  typename,
  commandInstanceId,
  context,
}: {
  typename: string;
  commandInstanceId: string;
  context?: CreateNodeContextType;
}) {
  const store = getStore(commandInstanceId);
  const dialogLayoutMode = State((state) => state.dialogLayoutMode);

  useNodeSetup({
    mode: "create",
    typename: typename,
    store: store,
    watchVariables: [],
    isPersistedOnExit: true,
    isClearedOnScopedEntityChange: true,
  });

  const { fieldDefinitions } = useNode(store);
  const isSetup = store(
    useShallow(
      (state) => state.objectType != null && state.fieldDefinitions.length > 0,
    ),
  );

  // Seeded after setup, as ``FieldReferenceCreateCard`` does: setup resets the
  // values once the object definition lands. Only ``fieldValues`` is seeded,
  // so the seeded fields count as changes and are sent. The record is created
  // in the entity the context names, not whichever one is active.
  useEffect(() => {
    if (!isSetup) return;
    if (context?.entityIdentifier) {
      store.setState({ entityIdentifier: context.entityIdentifier });
    }
    if (context?.initialFieldValues) {
      store.setState({
        fieldValues: {
          ...store.getState().fieldValues,
          ...context.initialFieldValues,
        },
      });
    }
  }, [isSetup, store, context?.entityIdentifier, context?.initialFieldValues]);

  // Map dialogLayoutMode to NodeLayout's forceLayoutMode
  const forceLayoutMode = dialogLayoutMode === "split" ? "cards" : "tabs";

  return (
    <StickyLayout childrenClassName="flex-col max-w-[calc(100%)] w-[calc(100%)] h-[calc(100%)] overflow-y-auto overflow-x-hidden relative items-stretch flex-nowrap pt-0 pb-0">
      <NodeLayout
        store={store}
        fieldDefinitions={fieldDefinitions}
        isSingleClick={true}
        autofocus={true}
        forceLayoutMode={forceLayoutMode}
      />
    </StickyLayout>
  );
}

/** Extracted as a component so it can use hooks (useScope) at render time,
 *  avoiding the need to re-register the command definition when entity context changes. */
function CreateNodeContextPicker({
  typename,
  commandInstanceId,
  context,
}: {
  typename: string;
  commandInstanceId: string;
  context?: CreateNodeContextType;
}) {
  const { scopedActiveEntityIdentifier, activeEntityIdentifiers } = useScope();
  const updateCommandContext = State((state) => state.updateCommandContext);
  const entityIdentifier =
    context?.entityIdentifier || scopedActiveEntityIdentifier;

  return (
    <CommandCenterContextEntityPicker
      entityIdentifier={entityIdentifier}
      activeEntityIdentifiers={activeEntityIdentifiers}
      setEntityIdentifier={(identifier) => {
        updateCommandContext(commandInstanceId, {
          entityIdentifier: identifier,
        });
        State.setState({ scopedEntityIdentifier: identifier });
      }}
    />
  );
}

export function CreateNodeCommand({ typename }: { typename: string }) {
  const registerCommandDefinition = useGlobalStore(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = useGlobalStore(
    (state) => state.unregisterCommandDefinition,
  );
  const object = Objects?.[typename];
  const title = (object?.humanName || typename.replace("Object", ""))
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  const ReactIcon = object?.ReactIcon;
  // A business is made in none, and a report in its maker's own books
  // whichever is picked - the businesses it reads are a field of its own - so
  // neither is asked which business it is made in.
  const isMadeInABusiness =
    typename !== "EntityObject" && typename !== "ReportObject";

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: `${COMMAND_ID_PREFIX}${typename}`,
      title: `Create ${title}`,
      description: "Create a new node",
      getLabel: (instanceId) => {
        return `Create ${title}`;
      },
      header: ({ commandInstanceId }) => (
        <Header commandInstanceId={commandInstanceId} />
      ),
      component: ({ commandInstanceId, context }) => (
        <Component
          typename={typename}
          commandInstanceId={commandInstanceId}
          context={context as CreateNodeContextType | undefined}
        />
      ),
      footer: ({ commandInstanceId, commandDefinitionId, context }) => (
        <Footer
          commandInstanceId={commandInstanceId}
          commandDefinitionId={commandDefinitionId}
          context={context as CreateNodeContextType | undefined}
        />
      ),
      context: ({ commandInstanceId, context }) => {
        if (!isMadeInABusiness) {
          return null;
        }
        return (
          <CreateNodeContextPicker
            typename={typename}
            commandInstanceId={commandInstanceId}
            context={context as CreateNodeContextType | undefined}
          />
        );
      },
      ReactIcon: ReactIcon,
      onClick: () => {
        // Bank accounts offer Plaid vs manual — route through the
        // chooser instead of jumping straight into the create form.
        if (typename === "FinancialAccountObject") {
          State.getState().pushToCommandStack({
            commandDefinitionId: "add-bank-account",
          });
          return;
        }
        if (isMadeInABusiness) {
          State.getState().pushToCommandStack({
            commandDefinitionId: SELECT_ENTITY_COMMAND_ID,
            context: {
              onSubmit: (entity) => {
                State.getState().pushToCommandStack({
                  commandDefinitionId: `${COMMAND_ID_PREFIX}${typename}`,
                  context: {
                    entityIdentifier: entity.identifier,
                  },
                });
              },
            },
          });
        } else {
          State.getState().pushToCommandStack({
            commandDefinitionId: `${COMMAND_ID_PREFIX}${typename}`,
            context: undefined,
          });
        }
      },
      onExit: (instanceId) => {
        stores.delete(instanceId);
      },
      size: "lg",
      isResizable: true,
      sizes: ["sm", "md", "lg"],
      getIsConfirmationRequiredOnExit: (instanceId) => {
        const store = getStore(instanceId);
        const isDirty = !_.isEqual(
          store.getState().fieldValues,
          store.getState().fieldValuesInitial,
        );
        return isDirty;
      },
    });

    return () => {
      unregisterCommandDefinition(`${COMMAND_ID_PREFIX}${typename}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typename]);

  return null;
}
